import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ArrowLeft, List, TrendingUp, Trash2, Pencil, Columns } from 'lucide-react';
import { Button } from './ui/button';
import PassViewGraph from './ViewGraph';
import ZoneViewDetailTable from './ZoneViewDetailTable';
import { useZoneViewDetails, useCurrentProject } from '../redux/WeldData/hooks/useWeldData';
import { updateWeldNumber } from '../redux/WeldData/actions';
import { UpdateWeldNumberDialog } from '../features/project/components/dashboard/production/UpdateWeldNumberDialog';
import { formatTimeForDisplay, formatDuration, formatTimestamp, formatDateTime } from '../lib/formatUtils';
import { formatTimestampForDisplay, convertWeldDataValue, getParameterUnitSuffix, getUnitFromSettings } from '../utils/unitUtils';
import { extractM500ZoneDetailsFromData } from '../utils/m500ZoneUtils';
import { ColumnSelectorModal, type ColumnDefinition } from './ui/ColumnSelectorModal';
import { createUpdateUserTableLayout, getAllUserTableLayouts } from '../redux/UserTableLayout/actions';

// Define the ZoneViewRow interface locally
interface ZoneViewRow {
  id: number;
  projectId?: number;
  projectName?: string;
  welding_system?: string;
  jobNumber?: string;
  status: string;
  zone_status_by_instantaneuous?: string;
  avg_zone_status?: string;
  stationName: string;
  bugType: string;
  torch: string;
  pass: string;
  zone: string;
  // M500 specific fields
  zone_name?: string;
  m500_zone?: string;
  pipe_name?: string;
  pass_name?: string;
  weldStartTime: string;
  weldEndTime: number | null;
  weldTime: number | null;
  autoincrement: string;
  welduid_s_event_torch?: string;
  bugNumber?: string;
  bandDiameter?: number | null;
  wireDiameter?: number | null;
  loggingDistance?: number | null;
  pipeDiameter?: number | null;
  current: { min: number | null; avg: number | null; max: number | null };
  volt: { min: number | null; avg: number | null; max: number | null };
  travelSpeed: { min: number | null; avg: number | null; max: number | null };
  trueEnergy: { min: number | null; avg: number | null; max: number | null };
  heat: { min: number | null; avg: number | null; max: number | null };
  wireSpeed: { min: number | null; avg: number | null; max: number | null };
  frequency: { min: number | null; avg: number | null; max: number | null };
  target: { min: number | null; avg: number | null; max: number | null };
  horizontalBias: { min: number | null; avg: number | null; max: number | null };
  totalWireConsumed: { min: number | null; avg: number | null; max: number | null };
  arcImpedance: { min: number | null; avg: number | null; max: number | null };
  oscillationWidth: { min: number | null; avg: number | null; max: number | null };
  passView?: any;
}

interface ZoneViewDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  zoneData: ZoneViewRow;
  weldData?: any; // Add weld data prop to access job_number
  statusCalculationMethod?: string;
  canEdit?: boolean;
}

// Column definitions for ZoneViewDetailTable (same structure as TiltViewDetailTable)
const ZONE_VIEW_DETAIL_COLUMNS: ColumnDefinition[] = [
  // Sticky columns
  { key: 'sl-no', label: 'Sl.no', category: 'Basic Info' },
  { key: 'status', label: 'Status', category: 'Basic Info' },
  { key: 'event', label: 'Event', category: 'Basic Info' },
  { key: 'time', label: 'Time', category: 'Basic Info' },
  // Scrollable columns
  { key: 'tilt', label: 'Tilt', category: 'Basic Info' },
  { key: 'pass', label: 'Pass', category: 'Basic Info' },
  { key: 'pipe', label: 'Pipe', category: 'Basic Info' },
  { key: 'zone', label: 'Zone', category: 'Basic Info' },
  { key: 'distance', label: 'Distance', category: 'Basic Info' },
  // Parameter columns
  { key: 'travel-speed', label: 'Travel Speed', category: 'Parameters' },
  { key: 'voltage', label: 'Voltage', category: 'Parameters' },
  { key: 'current', label: 'Current', category: 'Parameters' },
  { key: 'wire-speed', label: 'Wire Speed', category: 'Parameters' },
  { key: 'oscillation-width', label: 'Oscillation Width', category: 'Parameters' },
  { key: 'target', label: 'Target', category: 'Parameters' },
  { key: 'horizontal-bias', label: 'Horizontal Bias', category: 'Parameters' },
  { key: 'frequency', label: 'Frequency', category: 'Parameters' },
  { key: 'total-wire-consumed', label: 'Total Wire Consumed', category: 'Parameters' },
  { key: 'true-energy', label: 'True Energy', category: 'Parameters' },
  { key: 'heat', label: 'Heat', category: 'Parameters' },
  { key: 'head-angle', label: 'Head Angle', category: 'Parameters' },
  { key: 'arc-impedance', label: 'Arc Impedance', category: 'Parameters' },
];

// Table name for API
const TABLE_NAME = 'zoneViewDetail';

// Helper function to get all column keys
const getAllColumnKeys = (columns: ColumnDefinition[]): string[] => {
  const keys: string[] = [];
  columns.forEach(col => {
    keys.push(col.key);
    if (col.subColumns) {
      col.subColumns.forEach(sub => keys.push(sub.key));
    }
  });
  return keys;  
};

const ZoneViewDetailModal: React.FC<ZoneViewDetailModalProps> = ({ isOpen, onClose, zoneData, weldData, statusCalculationMethod, canEdit = true }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('table');
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [showAllInfo, setShowAllInfo] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [weldToUpdate, setWeldToUpdate] = useState<any>(null);

  const updateWeldNumberLoading = useSelector((state: any) => state.weldData?.updateWeldNumberLoading || false);
  const updateWeldNumberSuccess = useSelector((state: any) => state.weldData?.updateWeldNumberSuccess || false);

  const { zoneViewDetails, zoneViewDetailsLoading, zoneViewDetailsError, loadZoneViewDetails, clearZoneViewDetailsData } = useZoneViewDetails();
  const { currentProject } = useCurrentProject();

  // Get user table layouts from Redux state
  const userTableLayouts = useSelector((state: any) => state.userTableLayout?.layouts || []);
  const layoutsLoading = useSelector((state: any) => state.userTableLayout?.loading || false);

  // Store layout ID
  const layoutIdRef = useRef<number | null>(null);

  // Store default columns from API (for Reset button in ColumnSelectorModal)
  const [defaultColumnsFromAPI, setDefaultColumnsFromAPI] = useState<string[] | null>(null);

  // Column visibility state - initialize with all columns, will be updated from API
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => 
    getAllColumnKeys(ZONE_VIEW_DETAIL_COLUMNS)
  );

  // Column selector modal visibility state
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);

  // Load layouts from API when component mounts/opens
  useEffect(() => {
    if (isOpen) {
      // Saga will get userId from Redux state
      dispatch(getAllUserTableLayouts({ 
        table_name: 'zoneViewDetail'
      }));
    }
  }, [isOpen, dispatch]);

  // Update column state when layouts are loaded from API
  useEffect(() => {
    if (userTableLayouts.length > 0) {
      // Find layout for zone view detail (saga already filtered by user_id)
      const layout = userTableLayouts.find(
        (layout: any) => layout.table_name === TABLE_NAME
      );

      // Update layout ID, columns, and default columns from API
      if (layout) {
        layoutIdRef.current = layout.user_table_layout_id;
        if (layout.layout?.columns && Array.isArray(layout.layout.columns)) {
          setVisibleColumns(layout.layout.columns);
          // Store as default columns for Reset button
          setDefaultColumnsFromAPI(layout.layout.columns);
        }
      }
    }
  }, [userTableLayouts]);

  // Column visibility handler - save to API
  const handleColumnsSave = useCallback((selectedColumns: string[]) => {
    setVisibleColumns(selectedColumns);
    
    const payload: any = {
      table_name: TABLE_NAME,
      layout: {
        columns: selectedColumns
      }
    };
    
    // If we have an existing layout ID, include it for update
    if (layoutIdRef.current) {
      payload.user_table_layout_id = layoutIdRef.current;
    }
    
    // Saga will add userId from Redux state
    dispatch(createUpdateUserTableLayout(payload));
  }, [dispatch]);

  // Update layout ID when create/update succeeds
  const createUpdateSuccess = useSelector((state: any) => state.userTableLayout?.currentLayout);
  useEffect(() => {
    if (createUpdateSuccess && createUpdateSuccess.table_name === TABLE_NAME) {
      layoutIdRef.current = createUpdateSuccess.user_table_layout_id;
    }
  }, [createUpdateSuccess]);

  // Console log the zoneData object for debugging
  useEffect(() => {
    if (zoneData) {
    }
  }, [zoneData]);

  // Console log the zoneViewDetails object for debugging
  useEffect(() => {
    if (zoneViewDetails) {
    }
  }, [zoneViewDetails]);

  // Console log the weldData object for debugging
  useEffect(() => {
    if (weldData) {
    }
  }, [weldData]);


  // Generate welduid_s_event_torch from zone data
  const generateWelduidEventTorch = (zone: ZoneViewRow) => {
    // Based on the curl command example: "123456789012345678901234_1745759759_LEAD"
    // The format is: weld_uid_event_torch
    
    // Extract weld_uid from zone data - prefer bugNumber as it's likely the weld identifier
    let weldUid = zone.bugNumber || zone.id?.toString() || '123456789012345678901234';
    
    // Extract event - try to use start time timestamp first, then fallback to zone id
    let event = '1745759759'; // Default fallback
    if (zone.weldStartTime) {
      try {
        const startDate = new Date(zone.weldStartTime);
        const timestamp = Math.floor(startDate.getTime() / 1000);
        event = timestamp.toString();
      } catch (error) {

        // Fallback to zone id if timestamp parsing fails
        event = zone.id?.toString() || '1745759759';
      }
    } else if (zone.id) {
      // Use zone id as event if no start time
      event = zone.id.toString();
    }
    
    // Extract torch type from zone data
    let torch = 'LEAD'; // Default fallback
    if (zone.torch && zone.torch !== '-') {
      torch = zone.torch.toUpperCase();
    }
    
    const welduid_s_event_torch = `${weldUid}_${event}_${torch}`;
    return welduid_s_event_torch;
  };

  // Load zone view details when component mounts or when zoneData changes
  useEffect(() => {
    if (isOpen && zoneData) {
      // Use the welduid_s_event_torch from the zone data if available
      if (zoneData.welduid_s_event_torch) {
        // Determine torch type from zone data
        const torchType = zoneData.torch && zoneData.torch !== '-' ? zoneData.torch : 'Lead';
        
        // Use centralized project data - no need to pass projectId and projectName
        // Use zone_name for M500 systems, fallback to zone for others
        const zoneParam = zoneData.zone_name || zoneData.zone;
        loadZoneViewDetails(zoneData.welduid_s_event_torch, 'Zone', torchType, zoneParam);
      } else {
        // Fallback to construction if not available
        const welduidEventTorch = generateWelduidEventTorch(zoneData);
        const torchType = zoneData.torch && zoneData.torch !== '-' ? zoneData.torch : 'Lead';
        // Use centralized project data - no need to pass projectId and projectName
        // Use zone_name for M500 systems, fallback to zone for others
        const zoneParam = zoneData.zone_name || zoneData.zone;
        loadZoneViewDetails(welduidEventTorch, 'Zone', torchType, zoneParam);
      }
    }

    // Cleanup when component unmounts or closes
    return () => {
      if (!isOpen) {
        clearZoneViewDetailsData();
      }
    };
  }, [isOpen, zoneData, loadZoneViewDetails, clearZoneViewDetailsData]);

  // Reset selected row when data changes
  useEffect(() => {
    setSelectedRowIndex(null);
  }, [zoneViewDetails?.data?.data]);

  // Helper function to get GPS coordinates for display
  const getGPSCoordinates = (weldData: any) => {
    if (!weldData?.gps) return '-';
    
    try {
      let gpsInfo = weldData.gps;
      if (typeof weldData.gps === 'string') {
        gpsInfo = JSON.parse(weldData.gps);
      }
      
      const lat = gpsInfo?.Latitude || gpsInfo?.latitude;
      const lng = gpsInfo?.Longitude || gpsInfo?.longitude;
      
      if (lat && lng) {
        return `${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}`;
      }
    } catch (error) {
    }
    
    return '-';
  };

  // Helper function to get fixed column details
  const getFixedColumnDetails = (row: any) => {
    if (!row) return [];
    
    const details: Array<{ label: string; value: string }> = [];
    
    // Fixed columns from the table - include passView data as fallback
    details.push({ label: 'Pass Name', value: row.pass || row.pass_name || zoneData.pass || '-' });
    details.push({ label: 'Status', value: row.status || row.instantaneous_status || zoneData.status || '-' });
    details.push({ label: 'Station', value: row.station_name || zoneData.stationName || '-' });
    details.push({ label: 'Bug Type', value: row.bug_type || zoneData.bugType || '-' });
    details.push({ label: 'Torch', value: row.torch || zoneData.torch || '-' });
    details.push({ label: 'Bug Number', value: row.bug_number || zoneData.bugNumber || zoneData.passView?.bug_number || '-' });
    details.push({ label: 'GPS (deg)', value: getGPSCoordinates(weldData) });
    details.push({ label: 'Auto Increment', value: row.auto_increment || zoneData.autoincrement || zoneData.passView?.auto_increment || '-' });
    details.push({ label: 'Weld Time', value: row.weld_time ? String(formatDuration(row.weld_time)) : zoneData.weldTime ? String(formatDuration(zoneData.weldTime)) : '-' });
    
    // Additional fields that might be available
    if (row.event) details.push({ label: 'Event', value: row.event });
    if (row.job_number) details.push({ label: 'Job Number', value: row.job_number });
    if (row.time) details.push({ label: 'Time', value: formatTimestamp(row.time) });
    if (row.tilt !== undefined) details.push({ label: 'Tilt', value: row.tilt.toString() });
    
    return details;
  };

  // Helper function to get parameter details with min/avg/max
  const getParameterDetails = (row: any) => {
    if (!row) return [];
    
    const details: Array<{ label: string; min: string; avg: string; max: string }> = [];
    
    // Helper function to format min/avg/max object or single value
    const formatMinAvgMax = (field: any, decimals: number = 2) => {
      if (field && typeof field === 'object' && (field.min !== undefined || field.avg !== undefined || field.max !== undefined)) {
        // Handle min/avg/max object structure
        const min = field.min !== null && field.min !== undefined ? field.min.toFixed(decimals) : '-';
        const avg = field.avg !== null && field.avg !== undefined ? field.avg.toFixed(decimals) : '-';
        const max = field.max !== null && field.max !== undefined ? field.max.toFixed(decimals) : '-';
        return { min, avg, max };
      } else if (field !== null && field !== undefined) {
        // Handle single value
        const value = typeof field === 'number' ? field.toFixed(decimals) : String(field);
        return { min: value, avg: value, max: value };
      }
      return { min: '-', avg: '-', max: '-' };
    };
    
    // Welding parameters - handle both object and single value formats
    const travelSpeed = formatMinAvgMax(row.travelSpeed || row.travel_speed, 1);
    details.push({
      label: 'Travel Speed (mm/min)',
      min: travelSpeed.min,
      avg: travelSpeed.avg,
      max: travelSpeed.max
    });
    
    const voltage = formatMinAvgMax(row.voltage || row.volts, 1);
    details.push({
      label: 'Voltage (V)',
      min: voltage.min,
      avg: voltage.avg,
      max: voltage.max
    });
    
    const current = formatMinAvgMax(row.current, 2);
    details.push({
      label: 'Current (A)',
      min: current.min,
      avg: current.avg,
      max: current.max
    });
    
    const wireSpeed = formatMinAvgMax(row.wireSpeed || row.wire_speed, 0);
    details.push({
      label: `Wire Speed (${getParameterUnitSuffix('wire_speed')})`,
      min: wireSpeed.min,
      avg: wireSpeed.avg,
      max: wireSpeed.max
    });
    
    const oscillationWidth = formatMinAvgMax(row.oscillationWidth || row.oscillation_width, 2);
    details.push({
      label: `Oscillation Width (${getParameterUnitSuffix('oscillation_width')})`,
      min: oscillationWidth.min,
      avg: oscillationWidth.avg,
      max: oscillationWidth.max
    });
    
    const heat = formatMinAvgMax(row.heat, 2);
    details.push({
      label: `Heat (${getParameterUnitSuffix('heat')})`,
      min: heat.min,
      avg: heat.avg,
      max: heat.max
    });
    
    const trueEnergy = formatMinAvgMax(row.trueEnergy || row.true_energy, 2);
    details.push({
      label: `True Energy (${getParameterUnitSuffix('true_energy')})`,
      min: trueEnergy.min,
      avg: trueEnergy.avg,
      max: trueEnergy.max
    });
    
    // Additional parameters that might be available
    const target = formatMinAvgMax(row.target, 0);
    details.push({
      label: 'Target (A)',
      min: target.min,
      avg: target.avg,
      max: target.max
    });
    
    const horizontalBias = formatMinAvgMax(row.horizontalBias || row.horizontal_bias, 2);
    details.push({
      label: 'Horizontal Bias (A)',
      min: horizontalBias.min,
      avg: horizontalBias.avg,
      max: horizontalBias.max
    });
    
    const frequency = formatMinAvgMax(row.frequency, 0);
    details.push({
      label: 'Frequency (cyc/min)',
      min: frequency.min,
      avg: frequency.avg,
      max: frequency.max
    });
    
    const totalWireConsumed = formatMinAvgMax(row.totalWireConsumed || row.total_wire_consumed, 2);
    details.push({
      label: `Total Wire Consumed (${getParameterUnitSuffix('total_wire_consumed')})`,
      min: totalWireConsumed.min,
      avg: totalWireConsumed.avg,
      max: totalWireConsumed.max
    });
    
    return details;
  };



  // Get the selected row or first row for details section
  const selectedRow = selectedRowIndex !== null ? zoneViewDetails?.data?.data[selectedRowIndex] : zoneViewDetails?.data?.data[0];

  // Keep title as DATA360 - don't change it
  // useEffect(() => {
  //   if (isOpen) {
  //     const jobNumber = weldData?.jobNumber?.[0] || selectedRow?.job_number || zoneData.bugNumber || zoneData.id || 'N/A';
  //     const passName = zoneData.passView?.pass || zoneData.pass || 'N/A';
  //     const stationName = zoneData.stationName || 'Station';
  //     const zoneName = selectedRow?.zone_name || zoneData.zone || 'N/A';
  //     const bugType = zoneData.bugType || 'N/A';
  //     const torch = zoneData.torch || 'N/A';
  //     
  //     const title = `Weld: ${jobNumber} | ${passName} > ${stationName} > ${zoneName} > ${bugType} > ${torch}`;
  //     document.title = title;
  //   } else {
  //     // Reset title when modal closes
  //     document.title = 'DATA360';
  //   }
  // }, [isOpen, weldData?.jobNumber, selectedRow?.job_number, selectedRow?.zone_name, zoneData]);



  // Get data from API response for header details
  const detailData = zoneViewDetails?.data?.data || [];
  const firstRow = detailData[0] || {};

  console.log(detailData)
  console.log(firstRow)
  // Function to get display zone name using same logic as Zone View table
  const getDisplayZoneName = (zoneData: ZoneViewRow, selectedRow?: any) => {
    // Check if it's an M500 system
    const isM500System = zoneData?.welding_system?.toLowerCase() === "m500";
    
    if (isM500System) {
      // For M500 systems, use the m500_zone field directly
      return zoneData.m500_zone || selectedRow?.m500_zone || "";
    } else {
      // Check if zone is a JSON string and parse it
      const rawZone = selectedRow?.zone_name || zoneData.zone_name || zoneData.zone || "";
      try {
        const zoneData = JSON.parse(rawZone);
        return zoneData.ZoneNum || rawZone;
      } catch {
        return rawZone || "-";
      }
    }
  };
  
  // Calculate weld time from API data if available
  const calculateWeldTime = () => {
    if (detailData.length > 1) {
      const firstTime = detailData[0]?.time;
      const lastTime = detailData[detailData.length - 1]?.time;
      if (firstTime && lastTime) {
        const duration = (lastTime - firstTime) / 60; // Convert to minutes
        return formatDuration(duration);
      }
    }
    return zoneData.weldTime ? formatDuration(zoneData.weldTime) : '-';
  };

  // Get dynamic unit labels for basic details
  const getUnitLabel = (parameter: string): string => {
    return getParameterUnitSuffix(parameter);
  };

  // Convert diameter values from inches to display unit
  const convertDiameterValue = (value: any): string => {
    if (value === undefined || value === null || value === '') return '-';
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numericValue)) return value;
    const convertedValue = convertWeldDataValue(numericValue);
    return convertedValue.toFixed(2);
  };
  console.log(zoneData)

  // Priority: Use zoneData first (when available), then passView data, then API data
  const details = [
    { label: 'Job Number', value: firstRow?.job_number || weldData?.jobNumber?.[0] || zoneData.bugNumber || zoneData.id || '-' },
    { label: 'Pass', value: zoneData.pass || zoneData.passView?.pass || firstRow.pass_name || '-' },
    { label: 'Zone', value: getDisplayZoneName(zoneData, selectedRow) || '-' },
    { label: 'Station', value: firstRow?.station_name || zoneData.stationName || zoneData.passView?.station_name || '-' },
    { label: 'Bug Type', value: firstRow?.bug_type || zoneData.bugType || zoneData.passView?.bug_type || '-' },
    { label: 'Torch', value: firstRow?.torch || zoneData.torch || zoneData.passView?.torch || '-' },
    { label: 'Bug Number', value: firstRow?.bug_number || zoneData.bugNumber || zoneData.passView?.bug_number || '-' },
    { label: `Band Diameter (${getUnitLabel('band_diameter')})`, value: convertDiameterValue(firstRow?.band_diameter || zoneData.bandDiameter || zoneData.passView?.band_diameter) },
    { label: `Wire Diameter (${getUnitLabel('wire_diameter')})`, value: convertDiameterValue(firstRow?.wire_diameter || zoneData.wireDiameter || zoneData.passView?.wire_diameter) },
    { label: `Logging Distance (${getUnitLabel('logging_distance')})`, value: convertDiameterValue(firstRow?.logging_distance || zoneData.loggingDistance || zoneData.passView?.logging_distance) },
    { label: `Pipe Diameter (${getUnitLabel('pipe_diameter')})`, value: convertDiameterValue(firstRow?.pipe_diameter || zoneData.pipeDiameter || zoneData.passView?.pipe_diameter) },
    { label: 'Weld Start Time', value: zoneData.weldStartTime ? formatDateTime(zoneData.weldStartTime) : (firstRow.time ? formatDateTime(firstRow.time * 1000) : '-') },
    { label: 'Weld End Time', value: zoneData.weldEndTime ? formatDateTime(zoneData.weldEndTime * 1000) : (detailData.length > 1 ? formatDateTime(detailData[detailData.length - 1].time * 1000) : '-') },
    { label: 'GPS (deg)', value: getGPSCoordinates(weldData) },
    { label: 'Auto Increment', value: zoneData.autoincrement || zoneData.passView?.auto_increment || firstRow.auto_increment || '-' },
    { label: 'Weld Time', value: zoneData.weldTime ? formatDuration(zoneData.weldTime) : calculateWeldTime() },
    { label: 'Weld UID', value: firstRow?.weld_uid || '-' },
    { label: 'Edge UID', value: firstRow?.edge_uid || '-' },
  ];

  // Color palette for badges (similar to index.tsx)
  const colorPalette = [
    'bg-green-200', 'bg-blue-100', 'bg-green-100', 'bg-red-100', 
    'bg-cyan-100', 'bg-teal-100', 'bg-indigo-100', 'bg-lime-100',
    'bg-pink-100', 'bg-rose-100', 'bg-emerald-100', 'bg-violet-100',
    'bg-amber-100', 'bg-sky-100', 'bg-stone-100', 'bg-slate-100',
    'bg-zinc-100', 'bg-purple-100', 'bg-orange-100', 'bg-yellow-100'
  ];

  // Convert details to infoItems with colors
  const infoItems = details.map((item, index) => ({
    ...item,
    bgColor: colorPalette[index % colorPalette.length]
  }));

  const visibleItems = showAllInfo ? infoItems : infoItems.slice(0, 7);

  // Handle update modal
  const handleOpenUpdateModal = (weld: any) => {
  //  console.log(weldData,weld)
  //  console.log(zoneData)
    setWeldToUpdate(weld);
    setUpdateModalOpen(true);
  };

  const handleCloseUpdateModal = () => {
    if (!updateWeldNumberLoading) {
      setUpdateModalOpen(false);
      setWeldToUpdate(null);
    }
  };

  // Close modal on successful update
  useEffect(() => {
    if (updateWeldNumberSuccess) {
      handleCloseUpdateModal();
    }
  }, [updateWeldNumberSuccess]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-hidden">
        <div className="flex flex-col h-full font-sans overflow-hidden">
          {/* Header */}
          <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between bg-white flex-shrink-0">
            <div className="flex items-center gap-4">
              <Button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                variant="outline"
              >
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </Button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold">{weldData?.weldNumber || zoneData.bugNumber || zoneData.id || '-'}</h1>
                <div className="text-xs sm:text-sm text-gray-600 mt-1 space-y-1">
                  <div className="flex flex-wrap gap-2">
                    {visibleItems.map((item, index) => (
                      <span key={index} className={`${item.bgColor} px-2 py-1 rounded text-xs`}>
                        {item.label}: {item.value}
                      </span>
                    ))}
                    {infoItems.length > 7 && (
                      <button
                        onClick={() => setShowAllInfo(!showAllInfo)}
                        className="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                      >
                        {showAllInfo ? 'Show Less' : `Show More (+${infoItems.length - 7})`}
                        <span className="text-xs">{showAllInfo ? '▲' : '▼'}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Show loader while data is loading */}
            {zoneViewDetailsLoading ? (
              <div className="flex-1 flex items-center justify-center bg-white">
                <div className="flex flex-col items-center space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EB1C37]"></div>
                  <p className="text-gray-600 text-lg font-medium">Loading zone view details...</p>
                  <p className="text-gray-400 text-sm">Please wait while we fetch the data</p>
                </div>
              </div>
            ) : zoneViewDetailsError ? (
              <div className="flex-1 flex items-center justify-center bg-white">
                <div className="flex flex-col items-center space-y-4 max-w-md text-center">
                  <div className="text-red-500 text-5xl">⚠️</div>
                  <p className="text-red-600 text-lg font-medium">Error loading zone view details</p>
                  <p className="text-gray-500 text-sm">{zoneViewDetailsError}</p>
                  <Button onClick={onClose} variant="outline">Close</Button>
                </div>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col">
                <div className="sticky top-0 bg-white rounded-t-lg z-10 border-b border-gray-200 flex-shrink-0">
                  <div className="flex items-center justify-between px-1 md:px-4 py-2">
                    <TabsList className="h-auto flex-1 justify-start gap-2 md:gap-4 lg:gap-8 border-b bg-transparent py-2 flex-wrap">
                      <TabsTrigger value="table" className="data-[state=active]:border-[#EB1C37] data-[state=active]:text-[#EB1C37] relative px-1 pb-3 pt-2 text-sm md:text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px] whitespace-nowrap">
                        <List className="inline-block mr-1 h-4 w-4 align-text-bottom" /> Data Analysis
                      </TabsTrigger>
                      <TabsTrigger value="graph" className="data-[state=active]:border-[#EB1C37] data-[state=active]:text-[#EB1C37] relative px-1 pb-3 pt-2 text-sm md:text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px] whitespace-nowrap">
                        <TrendingUp className="inline-block mr-1 h-4 w-4 align-text-bottom" /> Graph View
                      </TabsTrigger>
                    </TabsList>
                    {activeTab === 'table' && (
                      <div className="flex gap-2 px-2">
                        <Button
                          onClick={() => setIsColumnModalOpen(true)}
                          className="flex items-center gap-2"
                          title="Configure columns"
                        >
                          <Columns className="h-4 w-4" />
                          <span className="hidden sm:inline">Columns</span>
                        </Button>
                        {canEdit && (
                          <Button
                            title="Edit Weld Number"
                            onClick={() => handleOpenUpdateModal(weldData)}
                            disabled={!weldData}
                          >
                            <Pencil className="h-3 w-3" /> Edit Weld Number
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <TabsContent value="table" className="flex-1 min-h-0 overflow-hidden">
                  <div className="h-full px-4">
                    <ZoneViewDetailTable 
                      detailData={zoneViewDetails?.data?.data || []}
                      onRowSelect={setSelectedRowIndex}
                      selectedRowIndex={selectedRowIndex}
                      loading={zoneViewDetailsLoading}
                      statusCalculationMethod={statusCalculationMethod}
                      visibleColumns={visibleColumns}
                    />
                  </div>
                </TabsContent>
                <TabsContent value="graph" className="flex-1 min-h-0 overflow-hidden">
                  <div className="h-full overflow-y-auto">
                    <div className="p-4 pb-20">
                      <PassViewGraph
                        detailData={zoneViewDetails?.data?.data || []}
                        projectNumber={zoneData.bugNumber || zoneData.passView?.bug_number || ''}
                        projectName={getDisplayZoneName(zoneData, selectedRow)}
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </div>

          {/* Weld Number Update Modal */}
          <UpdateWeldNumberDialog
            open={updateModalOpen}
            onOpenChange={(open) => {
              if (!open) {
                handleCloseUpdateModal();
              }
            }}
            title="Update Weld Number"
            currentValue={weldToUpdate?.weldNumber || ''}
            showJobNumber={false}
            currentJobNumber={weldToUpdate?.wpsNumber || ''}    
            onConfirm={(newValue, newJobNumber) => {
              if (weldToUpdate && newValue && !updateWeldNumberLoading) {
                const currentProjectId = currentProject?.project?.projectId;
                if (currentProjectId) {
                  const payload = {
                    project_id: currentProjectId,
                    old_weld_number: weldToUpdate.weldNumber,
                    new_weld_number: newValue,
                    weld_uid: zoneData.welduid_s_event_torch,
                    isNewWeld: false,
                    oldJobNumber: weldToUpdate.wpsNumber,
                    newJobNumber: newJobNumber,
                  };
                  dispatch(updateWeldNumber(payload));
                }
              }
            }}
            loading={updateWeldNumberLoading}
            maxWidth="3xl"
          />
        </div>
      </DialogContent>

      {/* Column Selector Modal */}
      <ColumnSelectorModal
        isOpen={isColumnModalOpen}
        onClose={() => setIsColumnModalOpen(false)}
        onSave={handleColumnsSave}
        columns={ZONE_VIEW_DETAIL_COLUMNS}
        selectedColumns={visibleColumns}
        title="Select Columns - Zone View Detail"
        defaultColumns={defaultColumnsFromAPI || getAllColumnKeys(ZONE_VIEW_DETAIL_COLUMNS)}
      />
    </Dialog>
  );
};

export default ZoneViewDetailModal; 